<template>
  <Modal v-model="isOpen" :close="() => { setModal(false) }">
    <div class="lcu-modal lcu-modal-medium">
      <div class="lcu-media-modal-body " v-if="activeModule">
        <div class="row row-center">
            <header class="media-modal-header">{{ activeModule.title }}</header>
        </div>
        <div class="row row-center" v-if="mediaLoading">
          <SpinLoader />
        </div>
        <div class="row row-center" v-if="mediaError">
          <p class="lcu-danger-text">{{ mediaError }}</p>
        </div>
        <div class="row row-center" v-if="!mediaLoading && !mediaError && activeMedia">
            <span class="row lcu-center-text">
              Pista de audio {{ activeMedia.mediaNumber }} de {{ media.length }}
            </span>
            <VideoPlayer
                ref="mediaPlayer"
                @on-ready="onPlayerReady"
                @on-time-update="onTimeUpdate"
                audio
                :key="activeMedia.url"
                :options="{
                    controls: true,
                    height: 100,
                    fluid: false,
                    poster: '/assets/video-thumb.png',
                    preload: 'auto',
                    sources: [
                    {
                        src: activeMedia.url,
                        type: activeMedia.mimeType,
                    }
                    ]
                }" />
            <p v-if="media.length < 1">
                No hay pistas de audio disponibles para este módulo.
            </p>
            <div class="lcu-media-list" v-if="media.length > 0">
                <button
                    v-for="mediaItem in media"
                    :key="mediaItem.url"
                    @click.prevent="selectMedia(mediaItem)"
                    :class="{
                        'lcu-media-listItemActive': activeMedia.url === mediaItem.url
                    }"
                    class="lcu-media-listItem">
                    <div class="lcu-media-listRow">
                      <span>
                        <span class="audioIcon" />
                        Pista de audio {{ mediaItem.mediaNumber }}
                      </span>
                      <div class="lcu-item-progress-inline">
                        <div class="lcu-item-progress-track">
                          <div
                            class="lcu-item-progress-fill"
                            :style="{ width: getMediaItemProgressPercent(mediaItem) + '%' }" />
                        </div>
                        <span class="lcu-item-progress-percent">
                          {{ getMediaItemProgressPercent(mediaItem) }}%
                        </span>
                      </div>
                    </div>
                </button>
            </div>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script>
import SpinLoader from 'ç/loaders/spin/Spin.vue';
import VideoPlayer from 'ç/video-player/VideoPlayer.vue';

export default {
  name: 'media-player-modal',
  components: {
    SpinLoader,
    VideoPlayer,
  },
  data() {
    return {
      mediaLoading: false,
      mediaError: false,
      activeMedia: null,
      activeMediaType: null,
      media: [],
      shouldAutoPlay: false,
      progressStorageKey: 'lcuMedia.progress',
      mediaProgress: {},
    };
  },
  props: [
    'is-open',
    'active-module',
  ],
  watch: {
    isOpen(is) {
      if (is) {
        this.getMedia();
      }
    },
  },
  methods: {
    async getMedia() {
      this.mediaError = false;
      this.mediaLoading = true;
      try {
        const { audio } = await this.$students({
          method: 'getProgramMedia',
          payload: {
            moduleId: this.activeModule?.id,
          },
        });
        this.media = audio;
        this.activeMedia = audio?.[0] || null;
        this.mediaProgress = this.getProgressStorage();
        this.shouldAutoPlay = false;
        this.$log.info('audio files: ', audio);
      } catch (e) {
        this.mediaError = 'Hubo un error al completar este módulo. Por favor, inténtalo de nuevo.';
        this.$log.error(e.message || e);
      } finally {
        this.mediaLoading = false;
      }
    },
    selectMedia(mediaItem) {
      this.activeMedia = mediaItem;
      this.shouldAutoPlay = true;
      this.$nextTick(() => {
        this.resumeFromSavedTime();
      });
    },
    onPlayerReady() {
      if (!this.shouldAutoPlay) {
        return;
      }
      this.$nextTick(() => {
        window.setTimeout(() => {
          this.resumeFromSavedTime();
          if (this.$refs.mediaPlayer && typeof this.$refs.mediaPlayer.play === 'function') {
            this.$refs.mediaPlayer.play();
          }
        }, 50);
      });
      this.shouldAutoPlay = false;
    },
    onTimeUpdate(time) {
      this.saveProgress(time);
    },
    getProgressStorage() {
      try {
        const saved = localStorage.getItem(this.progressStorageKey);
        return saved ? JSON.parse(saved) : {};
      } catch (e) {
        return {};
      }
    },
    saveProgress(time) {
      if (!this.activeMedia?.url) {
        return;
      }
      const safeTime = Number(time) || 0;
      const duration = this.getPlayerDuration();
      const nextProgress = {
        time: safeTime,
        duration,
        updatedAt: Date.now(),
      };
      const storage = {
        ...this.mediaProgress,
        [this.activeMedia.url]: nextProgress,
      };
      this.mediaProgress = storage;
      try {
        localStorage.setItem(this.progressStorageKey, JSON.stringify(storage));
      } catch (e) {
        this.$log.error(e.message || e);
      }
    },
    getPlayerDuration() {
      const player = this.$refs.mediaPlayer?.player;
      if (!player || typeof player.duration !== 'function') {
        return 0;
      }
      const duration = player.duration();
      if (Number.isFinite(duration) && duration > 0) {
        return Number(duration);
      }
      return 0;
    },
    getSavedProgress(url) {
      const saved = this.mediaProgress[url];
      if (typeof saved === 'number') {
        return {
          time: Number(saved) || 0,
          duration: 0,
        };
      }
      if (!saved || typeof saved !== 'object') {
        return {
          time: 0,
          duration: 0,
        };
      }
      return {
        time: Number(saved.time) || 0,
        duration: Number(saved.duration) || 0,
      };
    },
    getMediaItemProgressPercent(mediaItem) {
      if (!mediaItem?.url) {
        return 0;
      }
      const progress = this.getSavedProgress(mediaItem.url);
      if (!progress.duration || progress.duration <= 0) {
        return 0;
      }
      const raw = (progress.time / progress.duration) * 100;
      const clamped = Math.max(0, Math.min(100, raw));
      return Math.round(clamped);
    },
    resumeFromSavedTime() {
      if (!this.activeMedia?.url || !this.$refs.mediaPlayer || !this.$refs.mediaPlayer.player) {
        return;
      }
      const progress = this.getSavedProgress(this.activeMedia.url);
      if (progress.time > 0) {
        this.$refs.mediaPlayer.player.currentTime(progress.time);
      }
    },
    setModal(val) {
      this.$emit('set-modal', val);
    },
  },
};
</script>

<style scoped lang="scss">
.lcu-media-list {
    padding: 10px 0;
}

.audioIcon {
  &:after {
    display: inline-block;
    margin-right: 10px;
    font-family: 'lcu';
    content: '\e82c';
    font-weight: bold;
    width: 10px;
  }
}

.media-modal-header {
    font-size: 18px;
    font-weight: bold;
}

.lcu-media-listRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}

.lcu-item-progress-inline {
  display: flex;
  align-items: center;
  gap: 6px;
}

.lcu-item-progress-track {
  width: 72px;
  height: 5px;
  border-radius: 999px;
  background-color: rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

.lcu-media-listItem {
    margin-bottom: 5px;
    background-color: transparent;
    border: 1px solid $page-border;
    border-radius: 5px;
    padding: 10px 25px;
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    width: 100%;
    text-align: left;

    &:hover {
      background-color: $legacy-primary;
      color: $white;
    }

    &:last-child {
        margin-bottom: 0;
    }
}

.lcu-media-listItemActive {
    background-color: $legacy-primary;
    color: white;
}

.lcu-item-progress-fill {
  height: 100%;
  background-color: $legacy-primary;
  transition: width 0.25s ease;
}

.lcu-media-listItemActive {
  background-color: $legacy-primary;
  color: white;

  .lcu-item-progress-fill {
    background-color: $white;
  }

  &:hover {
    background-color: $legacy-primary;
    color: $white;
  }
}

.lcu-media-listItem {
  &:hover {
      .lcu-item-progress-fill {
        background-color: $white;
      }
  }
}

.lcu-item-progress-percent {
  font-size: 11px;
  font-weight: bold;
  min-width: 32px;
  text-align: right;
}

.video-js {
  height: 150px;
  width: 100%;
}
</style>
